// [PrintTSCode]
// [PrintTSComments]
/*
 * ENetUC_PresenceV2.ts
 * "UC-Server-Access-Protocol-PresenceV2" ASN.1 stubs.
 * This file was generated by estos esnacc 4.x
 * based on Coral WinSnacc written by Deepak Gupta
 * NOTE: This is a machine generated file - editing not recommended
 */

// prettier-ignore
/* eslint-disable */

/**
 * Presence interface (Version 2)
 * ## Module description
 * This module defines structures and operations for the presence V2 interface.
 * A version 2 of the presence interface was necessary to improve the amount of contact and service data.
 * The presence shows at any time the current availability of the person you wish to communicate with. For a detailed description see the help of the UCClient.
 * The following presence states are available:
 * - Available (green)
 * - Busy (red)
 * - Absent (yellow)
 * - Inactive (green\/yellow)
 * - Don't disturb  (red with a white stripe)
 * - Offline (gray)
 *
 * Additionally the API delivers information in form of contact data, service states and much more.
 * With all these infromations, it is possible to create an application, which give user the ability to see a detailed picture of a contact enriched with live state changes.
 *
 * The live changes are results of the different services the UCServer combines the presence state of the user:
 * - Telephone (via CTI)
 * - Calendar
 * - Audio\/Video
 * - Softphone
 * - Bluetooth device (locally attached to a client of the user).
 * - Manual input of the user
 *
 * ## Prerequisite for using the interface
 * - The client needs a socket for secure data Transmission between client-server and vise versa.
 * - Installation of ProCall Software in advance
 * - Then create a user for testing purposes
 * - login in this user name and check the functionality of this interface practically
 *
 *
 * ## Functionalities
 * - The status for client will be driven from various sources like:
 * - Digital Telephone, Mobile, Calender(ms outlook), Audio\/Video Chat, Manually changing Status and Computer system(login\/logout).
 * - The user has the permission to manually change the status
 * - The user can set a user-defined status (ex: Coffee Break)
 * - The user can set a couple of notices (ex: listing the daily appointments)
 * - The user can change status and notice
 * - The user can remove status and notice
 *
 *
 * ## Events
 * The Events that the client receives from the server are:
 *
 * \\- **[asnUpdatePresence](PresenceV2.asnUpdatePresence.html)** :
 * - It delivers the responses from Server to Client as per Client request
 * - The Server receives the specific request like [asnCustomNote](Common.type.AsnCustomNote.html) from Client
 * - The Client reciprocally receives an [asnUpdatePresence](PresenceV2.asnUpdatePresence.html) from Server
 * - The Update-Event will also be sent to those clients who are in touch with this client in the same Server
 * - It receives the Event from server in two forms whether an Argument or an Error
 * - As Argument: It handles the **Presence** and **iChangedFlags** events
 * \\- [Presence](PresenceV2.type.AsnUpdatePresenceArgument.html) : it shows updated data of the present-contact.
 * \\- [iChangedFlags](PresenceV2.type.AsnUpdatePresenceArgument.html) : it indicates that which area triggers the event and it's included because later on the server fills the fields
 * \\* check the [AsnPresence](PresenceV2.type.AsnPresence.html)(Active details) for more details
 *
 * \\- **[asnRemovePresence](PresenceV2.asnRemovePresence.html)** :
 * - It needs a contact id to remove the presence state
 * - It has an optionalParams for further implementation
 *
 * ## How the API works
 * Here is a very simple diagram of how the presence API works
 * @startuml
 * \"Client A\" -&gt; \"UCServer\": asnGetAndSubscribePresence(AsnGetAndSubscribePresenceArgument)
 * \"Client A\" &lt;- \"UCServer\": AsnGetAndSubscribePresenceResult
 * note left
 * Call asnGetAndSubscribePresence at least
 * the ContactIDs of the contacts the client
 * is interested to get updates from.
 * end note
 * ...
 * \"Client A\" &lt;- \"UCServer\": EVENT: asnUpdatePresence(AsnUpdatePresenceArgument)
 * note right
 * asnUpdatePresence are events from
 * the server with changes\/updates made to
 * the contact the client has subscribed to.
 * end note
 * ...
 * \"Client A\" -&gt; \"UCServer\": asnUnsubscribePresence(AsnUnsubscribePresenceArgument)
 * \"Client A\" &lt;- \"UCServer\": AsnUnsubscribePresenceResult
 * note left
 * Call asnUnsubscribePresence for
 * all contacts (ContactIDs) the server
 * should stop fireing events for.
 * end note
 * @enduml
 */

// [printTSImports]
import * as ENetUC_Common from "./ENetUC_Common";
import * as ENetUC_Common_AsnContact from "./ENetUC_Common_AsnContact";
import * as ENetUC_Common_Appointments from "./ENetUC_Common_Appointments";
import * as ENetUC_ClientCapabilities from "./ENetUC_ClientCapabilities";

// [PrintTSTypeDefCode] AsnPresenceAction
// [PrintTSEnumDefCode] AsnPresenceAction
export enum AsnPresenceAction {
	eAdded = 0,
	eChanged = 1,
	eDeleted = 2
}

// [PrintTSTypeDefCode] AsnPresenceSubscriptionFlags
// [PrintTSEnumDefCode] AsnPresenceSubscriptionFlags
export enum AsnPresenceSubscriptionFlags {
	/** Send the calculated contact presence value (default: on) */
	ePresenceSubscriptionPresence = 1,
	/** Send the complete, static contact data (name, adress, etc.) (default: on) */
	ePresenceSubscriptionData = 2,
	/** Send the complete contact image (if there is one) not only the hash value (default: on) */
	ePresenceSubscriptionDataIncludeImage = 4,
	/** Send information about the contact configuration (changes about access rights etc.) (default: on) */
	ePresenceSubscriptionConfiguration = 8,
	/** Send information about the actual absent state. This is manual set presence, not the calculated one, which includes not only the absent state. (default: on) */
	ePresenceSubscriptionAbsentstate = 16,
	/** Send information about the note of the contact (default: on) */
	ePresenceSubscriptionNote = 32,
	/** Send information about appointments (default: on) */
	ePresenceSubscriptionAppointment = 64,
	/** Send information about the phone lines of the contact (default: on) */
	ePresenceSubscriptionPhoneLines = 128,
	/** Send information about the calls on the phone lines of the contact (default: on) */
	ePresenceSubscriptionPhoneCalls = 256,
	/** Send information about the forward states on the phone lines of the contact (default: on) */
	ePresenceSubscriptionPhoneForwards = 512,
	/** Send information about the contact right challenges if some contzact requests new contact rights */
	ePresenceSubscriptionMyChallengeUpdates = 1024,
	/** Send information about changes to the own authorization list */
	ePresenceSubscriptionDynamicRightsUpdate = 2048,
	/** Send information about changes to an authorization list on a group the logged in user is member of */
	ePresenceSubscriptionDynamicGroupRightsUpdate = 4096,
	/** Send informations about changes to the global authorisation list */
	ePresenceSubscriptionDynamicGlobalRightsUpdate = 8192,
	/** = 0x00004000, */
	ePresenceSubscriptionContactCapabilities = 16384,
	/** Send agent state events of other users. This is not part of the default value! */
	ePresenceSubscriptionAgentStatus = 32768,
	/** Send events by changes on the monitor grouping attributes. This is not part of the default value! */
	ePresenceSubScriptionMonitorGroupingAttribute = 65536
}

// [PrintTSTypeDefCode] AsnSetDynamicRightsErrorEnum
// [PrintTSEnumDefCode] AsnSetDynamicRightsErrorEnum
export enum AsnSetDynamicRightsErrorEnum {
	general = 0,
	permissiondenied = 1,
	writeprofiledenied = 2,
	unknownrights = 3
}

// [PrintTSTypeDefCode] AsnRemoveDynamicRightsErrorEnum
// [PrintTSEnumDefCode] AsnRemoveDynamicRightsErrorEnum
export enum AsnRemoveDynamicRightsErrorEnum {
	general = 0,
	permissiondenied = 1,
	writeprofiledenied = 2,
	unknownrights = 3
}

// [PrintTSTypeDefCode] EnumRequestImageResult
// [PrintTSEnumDefCode] EnumRequestImageResult
export enum EnumRequestImageResult {
	/** The requested image is attached in the result. */
	eIMAGEATTACHED = 0,
	/** The image has not changed according to the given hash and therefore there is no image in the result. */
	eIMAGEHASHNOTCHANGED = 1,
	/** There is no image fopr the given contact. */
	eNOIMAGEFOUND = 2
}

// [PrintTSTypeDefCode] AsnPresenceLine
// [PrintTSSeqDefCode] AsnPresenceLine
/**
 * The structure is used to describe a phone line of a contact.
 * The structure is used to describe a phone line of a contact, including it state, their number and URI, forwards on the line currently set.
 */
export class AsnPresenceLine {
	public constructor(that: AsnPresenceLine) {
		Object.assign(this, that);
	}

	public static initEmpty(): AsnPresenceLine {
		return new AsnPresenceLine({
			u8sLinePhoneNumber: "",
			u8sLineNumber: "",
			iState: 0,
			iDoNotDisturb: 0,
			iForwardFeaturesPBX: 0
		});
	}

	/** LineURI */
	public u8sLinePhoneNumber!: string;
	/** Lesbare Nummer der Leitung (extrahiert aus der LineURI) */
	public u8sLineNumber!: string;
	/** Line State */
	public iState!: number;
	/** Line is on DoNotDisturb (1) or not (0) */
	public iDoNotDisturb!: number;
	/** What kind of forwarding this line supports */
	public iForwardFeaturesPBX!: number;
	public optionalParams?: ENetUC_Common.AsnOptionalParameters;
}

// [PrintTSTypeDefCode] AsnPresenceCall
// [PrintTSSeqDefCode] AsnPresenceCall
/**
 * This structure describes the information about a call on a phone line.
 * This structure describes the information about a call on a phone line of a contact including the last action taken
 * by the call (added, removed, changed), the current call state, features\/functions available on the call, call type and
 * if available the remote contact.
 */
export class AsnPresenceCall {
	public constructor(that: AsnPresenceCall) {
		Object.assign(this, that);
	}

	public static initEmpty(): AsnPresenceCall {
		return new AsnPresenceCall({
			iAction: 0,
			u8sConnectionID: "",
			u8sLinePhoneNumber: "",
			u8sLineNumber: "",
			u8sCurrentNumberSC: "",
			iCallState: 0,
			iFeatures: 0,
			iOutbound: 0,
			iInternal: 0,
			iCallType: 0
		});
	}

	/** add\/remove\/change */
	public iAction!: number;
	/** Die ID des Calls */
	public u8sConnectionID!: string;
	/** LineURI der Leitung auf der der Call statt findet */
	public u8sLinePhoneNumber!: string;
	/** Lesbare Nummer der Leitung (extrahiert aus der LineURI) */
	public u8sLineNumber!: string;
	/** Rufnummer des Anrufers */
	public u8sCurrentNumberSC!: string;
	/** Zustand des Calls */
	public iCallState!: number;
	/** Was darf der EventgempfÃ¤nger mit dem Call machen */
	public iFeatures!: number;
	/** 1: Abgehend, 0: Ankommend */
	public iOutbound!: number;
	/** 1: Interner GesprÃ¤chspartner, 0: externer GesprÃ¤chspartner */
	public iInternal!: number;
	/** erstmal nur fuer unterscheidung controller oder nicht-controller */
	public iCallType!: number;
	/** Das nÃ¶tigste Ã¼ber den Anrufer soweit bekannt */
	public remoteContact?: ENetUC_Common.AsnNetDatabaseContact;
	public optionalParams?: ENetUC_Common.AsnOptionalParameters;
}

// [PrintTSTypeDefCode] AsnPresenceForward
// [PrintTSSeqDefCode] AsnPresenceForward
/**
 * This strutures describes the phone forward state of a phone line.
 * A phone line can be set to forward call automatically to other endpoints. This structure describes
 * the different forward states.
 */
export class AsnPresenceForward {
	public constructor(that: AsnPresenceForward) {
		Object.assign(this, that);
	}

	public static initEmpty(): AsnPresenceForward {
		return new AsnPresenceForward({
			u8sLinePhoneNumber: "",
			u8sLineNumber: "",
			iInternalID: 0,
			u8sDestination: "",
			iForwardMode: 0
		});
	}

	/** LineURI der betroffenen Leitung */
	public u8sLinePhoneNumber!: string;
	/** Lesbare Nummer der Leitung (extrahiert aus der LineURI) */
	public u8sLineNumber!: string;
	/** Interne ID des Forwards */
	public iInternalID!: number;
	/** Ziel der Umleitung */
	public u8sDestination!: string;
	/** Art der Umleitung */
	public iForwardMode!: number;
	public optionalParams?: ENetUC_Common.AsnOptionalParameters;
}

// [PrintTSTypeDefCode] AsnContactCapabilities
// [PrintTSSeqDefCode] AsnContactCapabilities
/**
 * Structure of client capabilities.
 * This structure has been superseeded by the AsnClientCapabilitiesV2 structure
 *
 * @deprecated *
 */
export class AsnContactCapabilities {
	public constructor(that: AsnContactCapabilities) {
		Object.assign(this, that);
	}

	public static initEmpty(): AsnContactCapabilities {
		return new AsnContactCapabilities({
			bAudio: false,
			bVideo: false,
			bScreenShare: false,
			bExchangeCalendar: false,
			bNotesCalendar: false,
			bCamControl: false
		});
	}

	/** At least one client has audio capabilities. */
	public bAudio!: boolean;
	/** At least one client has video capabilities (camera). */
	public bVideo!: boolean;
	/** At least one client has screen sharing capabilities. */
	public bScreenShare!: boolean;
	/** At least one client has calendar replication capabilities for Microsoft Exchange. */
	public bExchangeCalendar!: boolean;
	/** At least one client has calendar replication capabilities for Notes. */
	public bNotesCalendar!: boolean;
	/** At least one client makes it possible that his camera can be remote controlled (left, right, up, down, zoom in\/out). */
	public bCamControl!: boolean;
}

// [PrintTSTypeDefCode] AsnActualUserDynamicRights
// [PrintTSSeqDefCode] AsnActualUserDynamicRights
/**
 * Structure for AsnPresence to give the client a hint, whcih access rights are effective to set on an contact.
 * Minimum and maximum of the user dynamic rights which can be set to a contact. See more details in AsnPresence .
 */
export class AsnActualUserDynamicRights {
	public constructor(that: AsnActualUserDynamicRights) {
		Object.assign(this, that);
	}

	public static initEmpty(): AsnActualUserDynamicRights {
		return new AsnActualUserDynamicRights({
			asnMinPossibleRights: ENetUC_Common_AsnContact.AsnUserDynamicRights.initEmpty(),
			asnMaxPossibleRights: ENetUC_Common_AsnContact.AsnUserDynamicRights.initEmpty()
		});
	}

	/** The minimum of possible rights to set. */
	public asnMinPossibleRights!: ENetUC_Common_AsnContact.AsnUserDynamicRights;
	/** The maximum of possible rights to set. */
	public asnMaxPossibleRights!: ENetUC_Common_AsnContact.AsnUserDynamicRights;
	/** If not set, there are no rights set already. */
	public asnCurrentRights?: ENetUC_Common_AsnContact.AsnUserDynamicRights;
	/** No optional parameters yet. */
	public optionalParams?: ENetUC_Common.AsnOptionalParameters;
}

// [PrintTSTypeDefCode] AsnAgentState
// [PrintTSSeqDefCode] AsnAgentState
/**
 * Struture for AsnPresence of the actual agent state of the contact
 * To reflect the actual agent state of a contact which is a PBX or LiveChat Agent, this struture
 * contains the state and agentgroup list of the contact.
 */
export class AsnAgentState {
	public constructor(that: AsnAgentState) {
		Object.assign(this, that);
	}

	public static initEmpty(): AsnAgentState {
		return new AsnAgentState({
			dwState: 0
		});
	}

	/** AsnAgentStateEnum - the current agent state of the contact. */
	public dwState!: number;
	/** To be Documented - Agent Group List with states? */
	public mapDetails?: ENetUC_Common.AsnStringIntegerPairList;
	/** No optional parameters yet. */
	public optionalParams?: ENetUC_Common.AsnOptionalParameters;
}

// [PrintTSTypeDefCode] AsnRemovePresenceArgument
// [PrintTSSeqDefCode] AsnRemovePresenceArgument
/**
 * Argument for the event asnRemovePresence.
 * The argument contains only the ContactID if the contact whcih has been removed.
 */
export class AsnRemovePresenceArgument {
	public constructor(that: AsnRemovePresenceArgument) {
		Object.assign(this, that);
	}

	public static initEmpty(): AsnRemovePresenceArgument {
		return new AsnRemovePresenceArgument({
			u8sContactID: ""
		});
	}

	/** ContactID of the contact removed. */
	public u8sContactID!: string;
	/** No optional paramters yet. */
	public optionalParams?: ENetUC_Common.AsnOptionalParameters;
}

// [PrintTSTypeDefCode] AsnGetAndSubscribePresenceArgument
// [PrintTSSeqDefCode] AsnGetAndSubscribePresenceArgument
/**
 * Call this operation to get information about and events of another contact.
 * This operation enables the client to request informations and events about another contact.
 * The minimal parameter is a list of contact IDs (e.g. 'sip:tester@test.com'). These contacts
 * will be searched in the ucserver and the service will return contact data back in the result
 * and send events about changes from there on.
 *
 * The data returned and events which will be send are depending on access rights and accessability on the contact data.
 *
 * Which events and return values are necessary for the client can be adjusted by the iEventFlagsToSubscribe and
 * iContactDetailsReturnFilter members.
 *
 * **Note**: Federation contacts (contgacts managed on another server as the connected one) may not return most of the data
 * even the bits are set. This is because the ucserver has to contact the appropriate server first and this can take some time.
 * To prevent long response times, the data which will be eventually given by the appropriate server will be send back via event.
 *
 * If the client wants to prevent SIP or XMPP fedeation contacts, it can set bOpenOnlyInternalContacts to true.
 *
 * If the client wants to prevent requesting new contact rights on the contact it can set bDontRequestPermissions to true.
 */
export class AsnGetAndSubscribePresenceArgument {
	public constructor(that: AsnGetAndSubscribePresenceArgument) {
		Object.assign(this, that);
	}

	public static initEmpty(): AsnGetAndSubscribePresenceArgument {
		return new AsnGetAndSubscribePresenceArgument({
			seqContactIDs: new ENetUC_Common.UTF8StringList()
		});
	}

	/** List of strings containing the contact ID of the requestet contact (e.g. 'sip:tester@test.com') */
	public seqContactIDs!: ENetUC_Common.UTF8StringList;
	/** AsnPresenceSubscriptionFlags - which events does the client wants to receive from the server. */
	public iEventFlagsToSubscribe?: number;
	/** AsnPresenceSubscriptionFlags - which return values are needed from the ucserver. */
	public iContactDetailsReturnFilter?: number;
	public bOpenOnlyInternalContacts?: boolean;
	/** Keine Berechtigungsanfrage starten */
	public bDontRequestPermissions?: boolean;
	/** (z.B. sollen anonyme Benutzer eben nicht alle Events bekommen kÃ¶nnen */
	public optionalParams?: ENetUC_Common.AsnOptionalParameters;
}

// [PrintTSTypeDefCode] AsnUnsubscribePresenceArgument
// [PrintTSSeqDefCode] AsnUnsubscribePresenceArgument
/**
 * Argument for stopping contact subscrptions on the ucserver.
 * To stop getting contact events from the ucserver with the operation asnUnsubscribePresence this sequence
 * contains the list of contact IDs to unsubscribe from.
 */
export class AsnUnsubscribePresenceArgument {
	public constructor(that: AsnUnsubscribePresenceArgument) {
		Object.assign(this, that);
	}

	public static initEmpty(): AsnUnsubscribePresenceArgument {
		return new AsnUnsubscribePresenceArgument({
			seqContactIDs: new ENetUC_Common.UTF8StringList()
		});
	}

	/** List of contact IDs to unsubscribe from (e.g. 'sip:tester@test.com'). */
	public seqContactIDs!: ENetUC_Common.UTF8StringList;
	/** For future use. */
	public optionalParams?: ENetUC_Common.AsnOptionalParameters;
}

// [PrintTSTypeDefCode] AsnUnsubscribePresenceResult
// [PrintTSSeqDefCode] AsnUnsubscribePresenceResult
/**
 * Result of asnUnsubscribePresence if the operation was executed successfully
 * This result sequence is send by the server if the unsibscribe wents successfull. iResult should always be 0.
 */
export class AsnUnsubscribePresenceResult {
	public constructor(that: AsnUnsubscribePresenceResult) {
		Object.assign(this, that);
	}

	public static initEmpty(): AsnUnsubscribePresenceResult {
		return new AsnUnsubscribePresenceResult({
			iResult: 0
		});
	}

	public iResult!: number;
	public optionalParams?: ENetUC_Common.AsnOptionalParameters;
}

// [PrintTSTypeDefCode] AsnSetDynamicRightsArgument
// [PrintTSSeqDefCode] AsnSetDynamicRightsArgument
/**
 * Argument for asnSetDynamicRights to set\/change access rights to contact data and its service informations
 * The argument must contain the contact ID of the contact which access rights list has to be modified, the contact ID
 * of the contact which gets\/looses the rights and the rights itself.
 *
 * The rights can be set with a constant value (iDynamicRights) or more specialized with the optional AsnUserDynamicRights member
 * (not recommended, only use it for very special edge cases).
 *
 * The values for iDynamicRights are part of AsnPresenceRightsFlags in the general module. The valid values are:
 * &lt;ul&gt;
 * &lt;li&gt;eBlocked (0)&lt;\/li&gt;
 * &lt;li&gt;ePublic (65536, 0x0001 0000)&lt;\/li&gt;
 * &lt;li&gt;eCompany (131072, 0x0002 0000)&lt;\/li&gt;
 * &lt;li&gt;eTeam (262144, 0x0004 0000)&lt;\/li&gt;
 * &lt;li&gt;ePersonal (524288, 0x0008 0000)&lt;\/li&gt;
 * &lt;\/ul&gt;
 */
export class AsnSetDynamicRightsArgument {
	public constructor(that: AsnSetDynamicRightsArgument) {
		Object.assign(this, that);
	}

	public static initEmpty(): AsnSetDynamicRightsArgument {
		return new AsnSetDynamicRightsArgument({
			u8sOwnerContactID: "",
			u8sRemoteContactID: "",
			iDynamicRights: 0
		});
	}

	/** ContactID of the contact who grants\/changes the rights. */
	public u8sOwnerContactID!: string;
	/** ContactID of the contact to give to\/change the rights. */
	public u8sRemoteContactID!: string;
	/** Valid values are (taken from AsnPresenceRightsFlags): */
	public iDynamicRights!: number;
	/** Only use this member to give access rights, which are not part of the predefined patterns.&lt;br \/&gt; */
	public dynamicRights?: ENetUC_Common_AsnContact.AsnUserDynamicRights;
}

// [PrintTSTypeDefCode] AsnSetDynamicRightsResult
// [PrintTSSeqDefCode] AsnSetDynamicRightsResult
/**
 * Result of asnSetDynamicRights if the operation was successful.
 * If asnSetDynamicRights was executed successful, this result will be returned and it will contain the resulting access rights list.
 */
export class AsnSetDynamicRightsResult {
	public constructor(that: AsnSetDynamicRightsResult) {
		Object.assign(this, that);
	}

	public static initEmpty(): AsnSetDynamicRightsResult {
		return new AsnSetDynamicRightsResult({
			iResult: 0,
			dynamicRights: ENetUC_Common_AsnContact.AsnUserDynamicRights.initEmpty()
		});
	}

	/** Should always be 0 (NO_ERROR). */
	public iResult!: number;
	/** The resulting rights in the rights list of the contact which rightslist had to be changed. This includes NOT the rights granted by global or group rights! */
	public dynamicRights!: ENetUC_Common_AsnContact.AsnUserDynamicRights;
}

// [PrintTSTypeDefCode] AsnRemoveDynamicRightsArgument
// [PrintTSSeqDefCode] AsnRemoveDynamicRightsArgument
/**
 * Argument for asnRemoveDynamicRights
 * The operation asnRemoveDynamicRights get only two parameters in its argument. u8sOwnerContactID of the contact which access list will be changed and
 * u8sRemoteContactID of the contact which will be removed from the list.
 */
export class AsnRemoveDynamicRightsArgument {
	public constructor(that: AsnRemoveDynamicRightsArgument) {
		Object.assign(this, that);
	}

	public static initEmpty(): AsnRemoveDynamicRightsArgument {
		return new AsnRemoveDynamicRightsArgument({
			u8sOwnerContactID: "",
			u8sRemoteContactID: ""
		});
	}

	/** ContactID - owner of the access list whoich will be changed */
	public u8sOwnerContactID!: string;
	/** ContactID - Contact which will be removed from the access list. */
	public u8sRemoteContactID!: string;
}

// [PrintTSTypeDefCode] AsnRemoveDynamicRightsResult
// [PrintTSSeqDefCode] AsnRemoveDynamicRightsResult
/**
 * Result of asnRemoveDynamicRights
 * The operation asnRemoveDynamicRights returns this result, if removing the dynmaic rights from the list was successful.
 */
export class AsnRemoveDynamicRightsResult {
	public constructor(that: AsnRemoveDynamicRightsResult) {
		Object.assign(this, that);
	}

	public static initEmpty(): AsnRemoveDynamicRightsResult {
		return new AsnRemoveDynamicRightsResult({
			iResult: 0
		});
	}

	/** RÃ¼ckgabewert ob das geklappt hat, wenn nicht != 0 */
	public iResult!: number;
}

// [PrintTSTypeDefCode] AsnRequestLargeProfileImageArgument
// [PrintTSSeqDefCode] AsnRequestLargeProfileImageArgument
/**
 * Argument for asnRequestLargeProfileImage
 * To get or check an large image of a contact, the client calls asnRequestContactImage with the ContactID (e.g. 'tester@test.com') or the AsnNetDatabaseContactID of the contact and
 * the known image hash the client already has. If the client has no hash, an empty string will indicate the server that
 * the client does not have an image yet.
 */
export class AsnRequestLargeProfileImageArgument {
	public constructor(that: AsnRequestLargeProfileImageArgument) {
		Object.assign(this, that);
	}

	public static initEmpty(): AsnRequestLargeProfileImageArgument {
		return new AsnRequestLargeProfileImageArgument({
			u8sContactID: "",
			u8sKnownHash: ""
		});
	}

	/** ContactID of the contact to get or check the image */
	public u8sContactID!: string;
	/** If the client has an image already, it sends its hash so the server can determine if the image has changed. */
	public u8sKnownHash!: string;
	/** Alternatively to u8sContactID one can use the AsnNetDatabaseContactID to identify the contact. */
	public netDatabaseContactID?: ENetUC_Common.AsnNetDatabaseContactID;
	/** No other optional parameters yet. */
	public optionalParams?: ENetUC_Common.AsnOptionalParameters;
}

// [PrintTSTypeDefCode] AsnRequestLargeProfileImageResult
// [PrintTSSeqDefCode] AsnRequestLargeProfileImageResult
/**
 * Result of asnRequestLargeProfileImage
 * If the operation asnRequestLargeProfileImage was executed successfully, this result will be send back to the client.
 * If there is no image or the image has not changed according the hash value given in the argument, iResult will contain the corresponding value from EnumRequestImageResult.
 * If the server will send back an image, sImageData will contain the binary data of the image and u8sImageHash the corresponding
 * new hash value for that image.
 */
export class AsnRequestLargeProfileImageResult {
	public constructor(that: AsnRequestLargeProfileImageResult) {
		Object.assign(this, that);
	}

	public static initEmpty(): AsnRequestLargeProfileImageResult {
		return new AsnRequestLargeProfileImageResult({
			iResult: 0
		});
	}

	/** EnumRequestImageResult - result of the call, if there is a new image, no image at all or the image hash has not changed. */
	public iResult!: number;
	/** If an image is send to the client, this field contains the binary data of the image. */
	public sImageData?: Uint8Array;
	/** If an image is send to the client, this field contains the corresponding hash value for that image. */
	public u8sImageHash?: string;
	/** No optional paramaters yet. */
	public optionalParams?: ENetUC_Common.AsnOptionalParameters;
}

// [PrintTSTypeDefCode] AsnRequestContactImageArgument
// [PrintTSSeqDefCode] AsnRequestContactImageArgument
/**
 * Argument for asnRequestContactImage
 * To get or check an image of a contact, the client calls asnRequestContactImage with the AsnNetDatabaseContactID of the contact and
 * the known image hash the client already has. If the client has no hash, an empty string will indicate the server that
 * the client does not have an image yet.
 */
export class AsnRequestContactImageArgument {
	public constructor(that: AsnRequestContactImageArgument) {
		Object.assign(this, that);
	}

	public static initEmpty(): AsnRequestContactImageArgument {
		return new AsnRequestContactImageArgument({
			netDatabaseContactID: ENetUC_Common.AsnNetDatabaseContactID.initEmpty(),
			u8sKnownHash: ""
		});
	}

	/** The contact identity of the contact of which the client wants to check or get the image of. */
	public netDatabaseContactID!: ENetUC_Common.AsnNetDatabaseContactID;
	/** If the client has an image already, it sends its hash so the server can determine if the image has changed. */
	public u8sKnownHash!: string;
	/** No optional parameters yet. */
	public optionalParams?: ENetUC_Common.AsnOptionalParameters;
}

// [PrintTSTypeDefCode] AsnRequestContactImageResult
// [PrintTSSeqDefCode] AsnRequestContactImageResult
/**
 * Result of asnRequestContactImage
 * If the operation asnRequestContactImage was executed successfully, this result will be send back to the client.
 * If there is no image or the image has not changed according the hash value given in the argument, iResult will contain the corresponding value from EnumRequestImageResult.
 * If the server will send back an image, sImageData will contain the binary data of the image and u8sImageHash the corresponding
 * new hash value for that image.
 */
export class AsnRequestContactImageResult {
	public constructor(that: AsnRequestContactImageResult) {
		Object.assign(this, that);
	}

	public static initEmpty(): AsnRequestContactImageResult {
		return new AsnRequestContactImageResult({
			iResult: 0
		});
	}

	/** EnumRequestImageResult - result of the call, if there is a new image, no image at all or the image hash has not changed. */
	public iResult!: number;
	/** If an image is send to the client, this field contains the binary data of the image. */
	public sImageData?: Uint8Array;
	/** If an image is send to the client, this field contains the corresponding hash value for that image. */
	public u8sImageHash?: string;
	/** No optional paramaters yet. */
	public optionalParams?: ENetUC_Common.AsnOptionalParameters;
}

// [PrintTSTypeDefCode] AsnMonitorGroupingAttribute
// [PrintTSSeqDefCode] AsnMonitorGroupingAttribute
/**
 * Future use
 */
export class AsnMonitorGroupingAttribute {
	public constructor(that?: AsnMonitorGroupingAttribute) {
		Object.assign(this, that);
	}

	public static initEmpty(): AsnMonitorGroupingAttribute {
		return new AsnMonitorGroupingAttribute();
	}

	/** Identification of grouping attribute set if it is defined by administration */
	public u8sAttributeGUID?: string;
	/** Short or abbreviation of the attribute */
	public u8sAttributeShort?: string;
	/** Attribute or \"Main\" attribute */
	public u8sAttributeLong?: string;
	/** Additional text or \"secondary\" attribute */
	public u8sAdditionalText?: string;
}

// [PrintTSTypeDefCode] AsnSetMonitorGroupingAttributeResult
// [PrintTSSeqDefCode] AsnSetMonitorGroupingAttributeResult
/**
 * Result of asnSetMonitorGroupingAttribute
 *
 * @private
 */
export class AsnSetMonitorGroupingAttributeResult {
	public constructor(that: AsnSetMonitorGroupingAttributeResult) {
		Object.assign(this, that);
	}

	public static initEmpty(): AsnSetMonitorGroupingAttributeResult {
		return new AsnSetMonitorGroupingAttributeResult({
			bSuccess: false
		});
	}

	public bSuccess!: boolean;
	public optionalParams?: ENetUC_Common.AsnOptionalParameters;
}

// [PrintTSTypeDefCode] AsnPresenceLines
// [PrintTSSetOfDefCode] AsnPresenceLines
export class AsnPresenceLines extends Array<AsnPresenceLine> { }

// [PrintTSTypeDefCode] AsnPresenceCalls
// [PrintTSSetOfDefCode] AsnPresenceCalls
export class AsnPresenceCalls extends Array<AsnPresenceCall> { }

// [PrintTSTypeDefCode] AsnPresenceForwards
// [PrintTSSetOfDefCode] AsnPresenceForwards
export class AsnPresenceForwards extends Array<AsnPresenceForward> { }

// [PrintTSTypeDefCode] AsnPresence
// [PrintTSSeqDefCode] AsnPresence
/**
 * This sequence contains the data and service states of a contact, called its 'presence'.
 * AsnPresence contains all the data and service states which represents the 'presence' of a contact.&lt;br \/&gt;
 * A client subscribing to the presence of a contact (via asnGetAndSubscribePresence ), will get back this structure filled according to his needs and access rights given.
 * This structure is also used for update events ( asnUpdatePresence ) and in this case only the changed values or sections will be enclosed.
 */
export class AsnPresence {
	public constructor(that: AsnPresence) {
		Object.assign(this, that);
	}

	public static initEmpty(): AsnPresence {
		return new AsnPresence({
			u8sContactId: ""
		});
	}

	/** ContactID of the contact which is responsible for this data */
	public u8sContactId!: string;
	/** Calculated presence of the contact. This is the over all presence of all the services which have an influnce on the current presence state of the contact (line, appointments, absent state a.s.o.) */
	public iPresenceState?: number;
	/** The contact data of the contact */
	public asnRemoteContact?: ENetUC_Common.AsnNetDatabaseContact;
	/** If the contact is an ucserver user, this may contain additional information about the user filtered by access rights. */
	public asnUserPropertyBag?: ENetUC_Common.AsnUserPropertyBag;
	/** Feature set on the contact. Bitfield with flags, which actions are possible on the contact (like \"set forwards\"). */
	public iContactFeatureSet?: number;
	/** Access rights on the contact. */
	public iStaticRights?: number;
	/** Access rights on the probably configured, additional lines of the contact. */
	public iMonitorAccessRights?: number;
	/** Actual set absent state. */
	public asnAbsentState?: ENetUC_Common_AsnContact.AsnAbsentState;
	/** Actual set note. */
	public asnCustomNote?: ENetUC_Common_AsnContact.AsnCustomNote;
	/** Actual appointments of the contact. */
	public seqActualAppointments?: ENetUC_Common_Appointments.SEQAppointmentList;
	/** First appointment in the future of the contact in a given time frame of 30 days. */
	public asnNextAppointment?: ENetUC_Common_Appointments.AsnAppointmentEntry;
	/** The phone lines of the contact. */
	public seqPhoneLines?: AsnPresenceLines;
	/** The current calls of the contact. */
	public seqCalls?: AsnPresenceCalls;
	/** The currently set phone forwards of the contact. */
	public seqLineForwards?: AsnPresenceForwards;
	public asnContactCapabilities?: AsnContactCapabilities;
	/** Capabilities in a simpler to expand format of the connected clients of the contact (audio, video, calendar replication etc.) */
	public asnContactCapabilitiesEx?: ENetUC_ClientCapabilities.AsnClientCapabilitiesV2;
	/** Minimum and maximum of the user dynamic rights which can be set to the contact.&lt;br \/&gt; */
	public asnCurrentUserDynamicRights?: AsnActualUserDynamicRights;
	/** Additional static user rights. See AsnUserStaticRights2. */
	public iStaticRights2?: number;
	/** Actual state of the user if he is an agent (via PBX\/ACD or LiveChat-Modules) */
	public asnAgentState?: AsnAgentState;
	/** Future use - If the user has a monitor grouping attribute set, it will be delivered here. */
	public asnMonitorGroupingAttribute?: AsnMonitorGroupingAttribute;
	/** Optional Params: */
	public optionalParams?: ENetUC_Common.AsnOptionalParameters;
}

// [PrintTSTypeDefCode] AsnPresenceList
// [PrintTSSetOfDefCode] AsnPresenceList
export class AsnPresenceList extends Array<AsnPresence> { }

// [PrintTSTypeDefCode] AsnUpdatePresenceArgument
// [PrintTSSeqDefCode] AsnUpdatePresenceArgument
/**
 * Argument of asnUpdatePresence
 * This argument contains the changes which triggered the event on the server and the new presence state of the corresponding contact.
 */
export class AsnUpdatePresenceArgument {
	public constructor(that: AsnUpdatePresenceArgument) {
		Object.assign(this, that);
	}

	public static initEmpty(): AsnUpdatePresenceArgument {
		return new AsnUpdatePresenceArgument({
			presence: AsnPresence.initEmpty(),
			iChangedFlags: 0
		});
	}

	/** Updated data and service states of the contact. See AsnPresence for more details. */
	public presence!: AsnPresence;
	/** Flags indicating which data sections od the contact have triggered the event. @linked AsnPresenceSubscriptionFlags\t\t\t\t\t\t\t\t\t\t\t\t\t\t */
	public iChangedFlags!: number;
	/** No optional parameters yet. */
	public optionalParams?: ENetUC_Common.AsnOptionalParameters;
}

// [PrintTSTypeDefCode] AsnGetAndSubscribePresenceResult
// [PrintTSSeqDefCode] AsnGetAndSubscribePresenceResult
/**
 * Result of asnGetAndSubscribePresence with the list of successfully subscribed contacts.
 * If the operation was successfully executed this result contains the list of successfully subscribed contacts, containing all requested data (if available).
 */
export class AsnGetAndSubscribePresenceResult {
	public constructor(that: AsnGetAndSubscribePresenceResult) {
		Object.assign(this, that);
	}

	public static initEmpty(): AsnGetAndSubscribePresenceResult {
		return new AsnGetAndSubscribePresenceResult({
			seqSubscribedPresence: new AsnPresenceList()
		});
	}

	/** Kontakte die angefordert wurden */
	public seqSubscribedPresence!: AsnPresenceList;
	public optionalParams?: ENetUC_Common.AsnOptionalParameters;
}

// [PrintTSTypeDefCode] AsnSetMonitorGroupingAttributeArgument
// [PrintTSSeqDefCode] AsnSetMonitorGroupingAttributeArgument
/**
 * Argument for asnSetMonitorGroupingAttribute
 *
 * @private
 */
export class AsnSetMonitorGroupingAttributeArgument {
	public constructor(that?: AsnSetMonitorGroupingAttributeArgument) {
		Object.assign(this, that);
	}

	public static initEmpty(): AsnSetMonitorGroupingAttributeArgument {
		return new AsnSetMonitorGroupingAttributeArgument();
	}

	/** Send a grouping attribute struct to the server to set texts or to change them. If you have am ID you can use attributeid to set the attribute. */
	public attribute?: AsnMonitorGroupingAttribute;
	/** No optionals yet. */
	public optionalParams?: ENetUC_Common.AsnOptionalParameters;
}
